/**
 * Tool Catalog — one index of every tool across all workbenches (Phase A4).
 *
 * Golden rule 4 (docs/architecture/single-app-integration-plan.md): a new
 * tool, command or shortcut is registered HERE first; workbench shells only
 * FILTER this catalog, they never hardcode tool lists. Because ids, command
 * names and shortcuts are declared in one place, cross-workspace conflicts
 * fail at registration time instead of surprising the user later.
 *
 * The catalog describes tools; it does not import their React hooks. The
 * shells that already mount tools (rendering/ToolScope, ui/Ribbon,
 * components/layout/Toolbar, the 2D command line) consume the filters
 * below. Migration of existing hardcoded lists is incremental Phase-B work.
 */

import type { ToolType } from './types';

/** Which workbench surface created / primarily drives a tool. */
export type ToolOwner = 'bim' | 'cad' | 'nurbs' | 'sketch';

/** Workbench shells that filter the catalog. */
export type WorkspaceId = 'bim' | '2d' | '3d' | 'sketch';

export interface ToolCatalogEntry {
    /** Canonical tool id, exactly as stored in useAppStore.activeTool. */
    tool: ToolType;
    owner: ToolOwner;
    /** Workspaces whose toolbar/ribbon should surface this tool. */
    workspaces: WorkspaceId[];
    /** Command-line names that activate the tool (CAD-style aliases). */
    commandNames?: string[];
    /** Keyboard shortcuts claimed by this tool (lowercase normalized). */
    shortcuts?: string[];
    /** Display label for shells that render buttons (toolbar). */
    label?: string;
    /** Toolbar grouping key ('nav' | 'draw' | 'modify' | '3d' | ...). */
    group?: string;
}

/**
 * Registry instance. Stored outside the zustand graph: catalog content is
 * build-time data (static entries) plus add-in registrations at boot, and
 * no render path should subscribe to it.
 */
const entries = new Map<ToolType, ToolCatalogEntry>();
const warnings: string[] = [];

/** Register (or update) one catalog entry. Conflicts are reported, not thrown. */
export function registerTool(entry: ToolCatalogEntry): void {
    const existing = entries.get(entry.tool);

    // Command-name collision check against every other registered tool.
    const usedNames = new Set<string>();
    entries.forEach((e, tool) => {
        if (tool === entry.tool) return;
        (e.commandNames ?? []).forEach((n) => usedNames.add(n.toUpperCase()));
    });
    (entry.commandNames ?? []).forEach((n) => {
        if (usedNames.has(n.toUpperCase())) {
            warnings.push(`[ToolCatalog] command '${n}' collides with another tool for '${entry.tool}'.`);
        }
    });

    // Shortcut collision check against every other registered tool.
    const usedShortcuts = new Map<string, ToolType>();
    entries.forEach((e, tool) => {
        if (tool === entry.tool) return;
        (e.shortcuts ?? []).forEach((s) => usedShortcuts.set(s.toLowerCase(), tool));
    });
    (entry.shortcuts ?? []).forEach((s) => {
        const clash = usedShortcuts.get(s.toLowerCase());
        if (clash) {
            warnings.push(`[ToolCatalog] shortcut '${s}' claimed by both '${clash}' and '${entry.tool}'.`);
        }
    });

    entries.set(entry.tool, { ...existing, ...entry, tool: entry.tool });
}

/** Batch registration for the built-in seed table. */
export function registerTools(list: ToolCatalogEntry[]): void {
    list.forEach(registerTool);
}

/** All registered entries. */
export function getToolCatalog(): ToolCatalogEntry[] {
    return Array.from(entries.values());
}

/** Filter: tools the given workspace shell should surface. */
export function toolsForWorkspace(workspace: WorkspaceId): ToolCatalogEntry[] {
    return getToolCatalog().filter((e) => e.workspaces.includes(workspace));
}

/** Reverse lookup: which tool does this command name activate (if any)? */
export function toolForCommandName(name: string): ToolCatalogEntry | undefined {
    const upper = name.toUpperCase();
    return getToolCatalog().find((e) => e.commandNames?.some((n) => n.toUpperCase() === upper));
}

/** Collision warnings accumulated during registration (for boot logging). */
export function getToolCatalogWarnings(): readonly string[] {
    return warnings;
}
